import { ClassValue, clsx } from 'clsx';
// import { twMerge } from 'tailwind-merge';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Combines multiple tailwind classes into a single class.
 * @param {ClassValue[]} classes - The classes to combine.
 * @returns {string} The combined classes.
 * @category Utils
 * @example
 * ```tsx
 * import { cn } from 'src/lib/utils';
 *
 * <button className={cn('bg-blue-500', 'text-white', isLoading && 'border-primary')}> Click me </button>
 * ```
 */

const twMerge = extendTailwindMerge({
  classGroups: {
    'font-size': [
      {
        text: [
          'header',
          'xs',
          'sm',
          'base',
          'lg',
          'h8',
          'h7',
          'h6',
          'h5',
          'h4',
          'h3',
          'h2',
          'h1',
        ],
      },
    ],
    colors: [
      {
        text: [
          'paragraph',
          'paragraph-lite',
          'title',
          'title-lite',
          'grayBg',
          'gradientColor',
          'body',
          'placeholder',
        ],
      },
    ],
  },
});

export function cn(...classes: ClassValue[]) {
  return twMerge(clsx(classes));
}

/**
 * Extracts the city name from a URL.
 * @param {string} url - The URL to extract the city name from.
 * @returns {string | null} The city name if found, otherwise null.
 * @category Utils
 * @example
 * ```tsx
 * import { extractCityNameFromUrl } from '@/lib/utils';
 *
 * const cityName = extractCityNameFromUrl('https://4takeaway.com/restaurants/city/berlin');
 * console.log(cityName); // 'berlin'
 * ```
 */

export function extractCityNameFromUrl(url: string): string | null {
  const pattern = /\/city\/([a-zA-Z]+)/;
  const match = pattern.exec(url);
  // If a match is found, extract and return the city name
  if (match && match[1]) {
    return match[1];
  } else {
    return null;
  }
}

function arrayMoveMutable(array: any, fromIndex: number, toIndex: number) {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

    const [item] = array.splice(fromIndex, 1);
    array.splice(endIndex, 0, item);
  }
}

/**
 * Moves an item in an array to a new index.
 * @param {any[]} array - The array to move the item in.
 * @param {number} fromIndex - The index of the item to move.
 * @param {number} toIndex - The index to move the item to.
 * @returns {any[]} The array with the item moved to the new index.
 * @category Utils
 * @example
 * ```tsx
 * import { arrayMove } from '@/lib/utils';
 *
 * const arr = [1, 2, 3, 4, 5];
 * const newArr = arrayMove(arr, 1, 3);
 * console.log(newArr); // [1, 3, 4, 2, 5]
 * ```
 */
export function arrayMove(array: any, fromIndex: number, toIndex: number) {
  array = [...array];
  arrayMoveMutable(array, fromIndex, toIndex);
  return array;
}
